import type { Rng } from '../rng';
import type { TableEnv } from './types';

/**
 * Luck at the House Special (decided 2026-09-23): the ★ should feel lucky
 * rather than pay extra, so a table's luck gives losing results a second
 * chance — the ball bounces again, the wheel ticks on, the race is re-run —
 * instead of raising payouts. Luck 0 is a normal table, 1 the floor's ★,
 * 2 the ★ with Liquid Courage I while Tipsy.
 *
 * Each game works out how often the second chance fires from its own odds,
 * so the table returns exactly LUCK_RTP[luck] (the balance sweep put the ★
 * at ~103%: drinkers win noticeably more, non-drinkers still almost never).
 */
// 104% -> 103% (2026-09-23, user: a 55% fresh-run win rate was too easy).
export const LUCK_RTP = [0, 1.03, 1.1] as const;

/** The table's luck (a hand-built env without `luck` counts the ★ as 1). */
export function luckOf(env: TableEnv): number {
  return env.luck ?? (env.special ? 1 : 0);
}

/** One result a play can have: its chance and what it returns per $1 bet. */
export type Outcome = readonly [chance: number, returns: number];

/**
 * How often a losing result (returns < 1) gets a second chance, so the
 * table returns LUCK_RTP[luck]. With second-chance rate q the return is
 * base + q * sum over losing results of p * (base - r): the re-draw is a
 * fresh play, worth `base` on average, in place of that loss.
 */
export function secondChanceRate(outcomes: readonly Outcome[], luck: number): number {
  if (luck <= 0) return 0;
  const base = outcomes.reduce((s, [p, r]) => s + p * r, 0);
  const gain = outcomes.reduce((s, [p, r]) => (r < 1 ? s + p * (base - r) : s), 0);
  if (gain <= 0) return 0;
  const target = LUCK_RTP[Math.min(luck, LUCK_RTP.length - 1)]!;
  return Math.max(0, Math.min(1, (target - base) / gain));
}

/** The return with second chances at rate q (for tests and the pane). */
export function returnWithSecondChance(outcomes: readonly Outcome[], q: number): number {
  const base = outcomes.reduce((s, [p, r]) => s + p * r, 0);
  return base + q * outcomes.reduce((s, [p, r]) => (r < 1 ? s + p * (base - r) : s), 0);
}

/**
 * Draws a result; if it loses, re-draws once with chance q. `lucky` is true
 * when the second chance turned a loss into a non-loss (worth a word on the
 * lens); `first` is then the loss it replaced, so the animation can show
 * it before the second chance.
 */
export function drawWithLuck<T>(rng: Rng, draw: () => T, loses: (outcome: T) => boolean, q: number): { outcome: T; lucky: boolean; first?: T } {
  const first = draw();
  if (q <= 0 || !loses(first) || rng.next() >= q) return { outcome: first, lucky: false };
  const second = draw();
  const lucky = !loses(second);
  return lucky ? { outcome: second, lucky, first } : { outcome: second, lucky };
}

/** How long one animation frame (one table tick) lasts on the lens, ms. A
 *  game whose own tick-driven animation follows casino frames starts it
 *  this much later per frame. */
export const FRAME_MS = 250;

/** The frame shown between a loss and its second chance. */
export function secondChanceFrame(lostView: string): string {
  return `${lostView}\n\n★ Second chance!`;
}

/** Games played in steps (a guess, a tile, a door) aim their luck so that
 *  cashing out after this many steps returns LUCK_RTP; each step past it
 *  adds a little more. */
export const STEP_BASIS = 2;

/** The table's target return at a luck level (1 = no luck). */
export function luckTarget(luck: number): number {
  return luck > 0 ? LUCK_RTP[Math.min(luck, LUCK_RTP.length - 1)]! : 1;
}

/**
 * A step that would fail is saved with this chance, so its success chance
 * goes from p to p * ratio (ratio > 1 is what luck is worth per step).
 */
export function stepSaveRate(p: number, ratio: number): number {
  if (ratio <= 1 || p <= 0 || p >= 1) return 0;
  return Math.max(0, Math.min(1, (p * ratio - p) / (1 - p)));
}
